import React, { useState, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, Phone, AlertCircle, CheckCircle, Upload, FileText, X } from 'lucide-react';
import Badge from '@/components/Badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const courseParam = searchParams.get('course');
  const { setAuthData } = useAuth();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [idFile, setIdFile] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a valid ID image (JPG, PNG, GIF) or PDF');
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setIdFile(file);
      setError('');
    }
  };

  const removeFile = () => {
    setIdFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!idFile) {
      setError('Please upload a copy of your ID (driver\'s license or state ID)');
      return;
    }

    setLoading(true);
    try {
      // Create FormData for file upload
      const formDataObj = new FormData();
      formDataObj.append('email', formData.email);
      formDataObj.append('password', formData.password);
      formDataObj.append('first_name', formData.first_name);
      formDataObj.append('last_name', formData.last_name);
      if (formData.phone) {
        formDataObj.append('phone', formData.phone);
      }
      formDataObj.append('id_document', idFile);

      const response = await axios.post(`${API_URL}/api/auth/register-with-id`, formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Set auth data manually since we're using a different endpoint
      const { access_token, user } = response.data;
      sessionStorage.setItem('token', access_token);
      sessionStorage.setItem('user', JSON.stringify(user));
      setAuthData(access_token, user);

      toast.success('Account created successfully! Your ID is pending verification.');
      const enrollUrl = courseParam ? `/enroll?course=${courseParam}` : '/enroll';
      navigate(enrollUrl);
    } catch (err) {
      console.error('Registration error:', err);
      let errorMsg = 'Registration failed. Please try again.';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
        }
      } else if (err.response?.status === 413) {
        errorMsg = 'File is too large. Please upload a smaller ID image (under 5MB).';
      } else if (err.response?.status === 422) {
        errorMsg = 'Please fill in all required fields (first name, last name, email, password, and ID document).';
      } else if (err.response?.status >= 500) {
        errorMsg = 'Server error. Please try again in a moment.';
      } else if (!err.response) {
        errorMsg = 'Network error. Please check your internet connection and try again.';
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1673814150804-630c3c088a34?crop=entropy&cs=srgb&fm=jpg&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 p-12 flex flex-col justify-end">
          <h2 className="text-4xl font-black text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            BEGIN YOUR <span className="text-primary">CERTIFICATION</span>
          </h2>
          <p className="text-white/80">
            Join the most comprehensive bail enforcement training in Colorado
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md py-8">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <Badge className="w-8 h-8" />
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              CBEC
            </span>
          </Link>

          <h1 className="text-3xl font-black mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            CREATE ACCOUNT
          </h1>
          <p className="text-muted-foreground mb-6">
            Enter your details to create your account
          </p>

          {/* Eligibility Requirements Box */}
          <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-sm">
            <h3 className="font-bold text-sm mb-3 text-primary uppercase tracking-wide">Colorado Eligibility Requirements</h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span><strong>Age:</strong> Must be at least 18 years old</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span><strong>Citizenship:</strong> U.S. citizen or resident alien</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span><strong>Residency:</strong> Must be a Colorado resident</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span><strong>Criminal Record:</strong> No felony convictions in last 10 years; 10 years clear from parole/probation</span>
              </li>
            </ul>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/30 rounded-sm flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="first_name"
                    name="first_name"
                    type="text"
                    required
                    placeholder="John"
                    className="pl-10"
                    value={formData.first_name}
                    onChange={handleChange}
                    data-testid="register-firstname-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="last_name">Last Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="last_name"
                    name="last_name"
                    type="text"
                    required
                    placeholder="Doe"
                    className="pl-10"
                    value={formData.last_name}
                    onChange={handleChange}
                    data-testid="register-lastname-input"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="john@example.com"
                  className="pl-10"
                  value={formData.email}
                  onChange={handleChange}
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone">Phone (Optional)</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="(555) 555-5555"
                  className="pl-10"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="register-phone-input"
                />
              </div>
            </div>

            {/* ID Upload Section */}
            <div>
              <Label htmlFor="id_document" className="flex items-center gap-1">
                Government ID <span className="text-destructive">*</span>
              </Label>
              <p className="text-xs text-muted-foreground mb-2">
                Upload a photo of your driver's license or state ID (required for Colorado compliance)
              </p>
              
              {!idFile ? (
                <div 
                  className="mt-1 border-2 border-dashed border-border rounded-sm p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    JPG, PNG, GIF or PDF (max 10MB)
                  </p>
                  <input
                    ref={fileInputRef}
                    id="id_document"
                    name="id_document"
                    type="file"
                    accept="image/*,.pdf"
                    className="hidden"
                    onChange={handleFileChange}
                    data-testid="register-id-input"
                  />
                </div>
              ) : (
                <div className="mt-1 border border-border rounded-sm p-4 flex items-center justify-between bg-primary/5">
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8 text-primary" />
                    <div>
                      <p className="text-sm font-medium">{idFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(idFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeFile}
                    className="p-1 hover:bg-destructive/10 rounded-sm transition-colors"
                  >
                    <X className="w-5 h-5 text-destructive" />
                  </button>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  required
                  placeholder="Minimum 8 characters"
                  className="pl-10"
                  value={formData.password}
                  onChange={handleChange}
                  data-testid="register-password-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  placeholder="Confirm your password"
                  className="pl-10"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary mt-6"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? 'CREATING ACCOUNT...' : 'CREATE ACCOUNT'}
            </Button>
          </form>

          <p className="text-center text-muted-foreground mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
